/**
 * Trial API
 *
 * API functions for complimentary trial signup functionality
 */

import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// ============================================
// TYPES
// ============================================

export interface TrialSettings {
  trial_enabled: boolean;
  trial_days: number;
  trial_terms_text: string;
  trial_requires_payment_method: boolean;
  business_name: string;
  logo_url: string;
  primary_color: string;
}

export interface TrialSettingsResponse {
  success: boolean;
  data: TrialSettings;
  message?: string;
}

export interface AdditionalContact {
  first_name?: string;
  last_name?: string;
  home_phone?: string;
  work_phone?: string;
  mobile_phone?: string;
  contact_type?: string;
}

export interface TrialAthlete {
  first_name: string;
  last_name: string;
  dob?: string;
  gender?: string;
}

export interface TrialSignupData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  tenant_id: string;
  alternate_email?: string;
  alternate_email_2?: string;
  mobile_sms?: string;
  carrier?: string;
  referral?: string;
  alternate_phone?: string;
  additionalContacts?: AdditionalContact[];
  // Athletes (at least 1 required)
  athletes: TrialAthlete[];
  // Trial class selection
  trial_program_id?: string;
  trial_slot_id?: string;
  trial_date?: string;
}

export interface TrialSignupResponse {
  success: boolean;
  message: string;
  data?: {
    lead_id?: string;
    membership_id?: string;
    email: string;
    stage?: string;
    trial_days?: number;
    trial_expires_at?: string;
    password_setup_link?: string;
  };
}

export interface TrialStatus {
  is_trial: boolean;
  status: string;
  trial_start_date: string | null;
  trial_expires_at: string | null;
  days_remaining: number;
  is_expired: boolean;
}

export interface TrialStatusResponse {
  success: boolean;
  data: TrialStatus;
  message?: string;
}

export interface TrialConvertResponse {
  success: boolean;
  message: string;
  data?: {
    membership_id: string;
    status: string;
  };
}

// ============================================
// API HELPERS
// ============================================

const getTrialHeaders = (token?: string) => ({
  "Content-Type": "application/json",
  authorization: token ? `Bearer ${token}` : "",
});

// ============================================
// PUBLIC APIs (No authentication required)
// ============================================

/**
 * Get trial settings for a tenant
 * Public endpoint - used to check if trial signup is enabled
 */
export const getTrialSettings = async (): Promise<TrialSettingsResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/trial/settings`, {
      headers: getTrialHeaders(),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {
        trial_enabled: false,
        trial_days: 14,
        trial_terms_text: "",
        trial_requires_payment_method: false,
        business_name: "",
        logo_url: "",
        primary_color: "#3b82f6",
      },
      message: err.response?.data?.message || err.message || "Failed to fetch trial settings",
    };
  }
};

/**
 * Submit trial signup
 * Public endpoint - creates trial membership without password
 */
export const submitTrialSignup = async (
  data: TrialSignupData
): Promise<TrialSignupResponse> => {
  try {
    const response = await axios.post(`${BASE_URL}/trial/signup`, data, {
      headers: getTrialHeaders(),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      message: err.response?.data?.message || err.message || "Failed to submit trial signup",
    };
  }
};

// ============================================
// PROTECTED APIs (Authentication required)
// ============================================

/**
 * Get current trial status for logged-in user
 * @param token - Authentication token
 */
export const getTrialStatus = async (token: string): Promise<TrialStatusResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/trial/status`, {
      headers: getTrialHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {
        is_trial: false,
        status: "unknown",
        trial_start_date: null,
        trial_expires_at: null,
        days_remaining: 0,
        is_expired: false,
      },
      message: err.response?.data?.message || err.message || "Failed to fetch trial status",
    };
  }
};

/**
 * Convert trial membership to active membership
 * @param token - Authentication token
 */
export const convertTrialToPaid = async (token: string): Promise<TrialConvertResponse> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/trial/convert`,
      {},
      {
        headers: getTrialHeaders(token),
      }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      message: err.response?.data?.message || err.message || "Failed to convert trial membership",
    };
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Calculate days remaining in trial
 * @param trialExpiresAt - ISO date string of trial expiration
 */
export const calculateDaysRemaining = (trialExpiresAt: string | null): number => {
  if (!trialExpiresAt) return 0;
  const expiresAt = new Date(trialExpiresAt);
  const now = new Date();
  const diffMs = expiresAt.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
};

/**
 * Check if trial is expired
 * @param trialExpiresAt - ISO date string of trial expiration
 */
export const isTrialExpired = (trialExpiresAt: string | null): boolean => {
  if (!trialExpiresAt) return true;
  return new Date(trialExpiresAt) < new Date();
};

/**
 * Format trial expiration date for display
 * @param trialExpiresAt - ISO date string of trial expiration
 */
export const formatTrialExpiration = (trialExpiresAt: string | null): string => {
  if (!trialExpiresAt) return "";
  const date = new Date(trialExpiresAt);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
